import instance from "../axios";

// ─── Types ────────────────────────────────────────────────────────────────────

export type SowStatus = "DRAFT" | "IN_REVIEW" | "FINAL";

export type SowTargetItem = {
  id: string;
  assetName: string;
  assessmentType: string;
  accessType: "graybox" | "blackbox" | "whitebox";
  targets: string;
  description?: string; // per-asset paragraph shown in the PDF scope section
  environment?: string; // e.g. Production, Staging
  status?: string; // e.g. Confirmed, In Scope
};

export type SowFields = {
  // Cover Page
  coverEngagementType?: string;
  coverDocumentTitle?: string;
  coverSubtitle?: string;
  coverPreparedFor?: string;
  coverPreparedBy?: string;
  coverDocumentReference?: string;
  coverVersionLabel?: string;
  coverClassification?: string;
  coverFooterNote?: string;
  footerReferenceNote?: string;
  innerPageHeaderText?: string;

  // Section 1 & 2 – Introduction & Objective
  introductionText?: string;
  objectiveText?: string;

  // Section 3 – Assets / Targets
  targetItems: SowTargetItem[];
  authorisedTargets: string;
  scopeBoundaryText?: string;
  rulesOfEngagementSummaryText?: string;

  // Section 4 – Testing Methodology & Coverage
  testingMethodologyText?: string;
  executionLifecycle?: { phase: string; description: string }[];
  coverageWebText?: string;
  coverageApiText?: string;
  coveragePaymentText?: string;
  coverageNetworkText?: string;

  // Section 5 – Timeline & Deliverables
  testingStartDate: string;
  testingEndDate: string;
  draftReportDate: string;
  finalReportDate: string;
  retestingEndDate: string;
  timelineAssumptionsText?: string;
  milestoneSchedule?: { milestone: string; schedule: string }[];
  deliverables?: { deliverable: string; description: string }[];
  ptaasPlatformText?: string;

  // Section 6 – Assumptions & Dependencies
  assumptions: string;
  clientDependenciesEnabled: boolean;
  clientDependencies: string;

  // Section 7 – Disclaimers & Professional Conduct
  disclaimersText?: string;

  // Section 8 – Exclusions
  standardExclusions?: string;
  additionalExclusions: string;

  // Section 9 – Commercial / Specific Terms
  engagementModel: "one-time" | "recurring" | "retainer";
  consultantDays: number | null;
  price: number | null;
  currency: "NZD" | "USD";
  paymentTerms: string;
  engagementStructure?: { phase: string; description: string; duration: string }[];
  generalTerms?: { sn: string; term: string; details: string }[];
  specificTerms?: { sn: string; term: string; details: string }[];

  // Section 9 – Delivery duration overrides
  indicativeDeliveryDuration: string;
  baselineAssessmentDuration: string;

  // Appendix A – Rules of Engagement
  appendixRoEIntro?: string;
  testingEnvironment: string;
  servicesOnInfrastructure?: string;
  publicAvailabilityDate?: string;
  thirdPartyAuthorisationsText?: string;
  testingSourceIp: string;
  wafRateLimitingNote?: string;
  testingWindow: string;
  blackoutPeriods: string;
  projectCompletionDateNote?: string;
  testAccountsNote: string;
  namedTesters: string;
  standardAccessAccount: string;
  privilegedAccessAccount: string;
  paymentTestingNote?: string;
  identityVerificationNote?: string;
  clientEmergencyContact: string;
  ctbEngagementLead: string;
  prohibitedActivitiesText?: string;
  stopTestingProcedureText?: string;

  // Section 10 – Acceptance
  clientSignerName: string;
  clientSignerTitle: string;
  clientEmail: string;
  clientPhone: string;
  clientSignedAt: string | null;
  ctbSignedAt: string | null;
  // Client billing details
  clientBillingName: string;
  clientRegisteredCompany: string;
  clientCountryOfIncorporation: string;
  clientBillingAddress: string;
  clientTaxNumber: string;
  clientBillingEmail: string;
  clientBillingPhone: string;
  ctbSignerName: string;
  ctbSignerTitle: string;
  ctbSignerPhone?: string;
  ctbSignerEmail?: string;

  // Optional in-platform CTB signature
  ctbSignatureBase64: string | null;
};

export type SowHistoryEntry = {
  version: number;
  savedAt: string;
  savedByUserId: number | null;
  savedByUsername?: string | null;
  fields: SowFields;
};

export type SowBlob = {
  fields: SowFields;
  history: SowHistoryEntry[];
};

export type SowState = {
  sow_status: SowStatus | null;
  sow_version: number;
  sow_data: SowBlob | null;
};

// ─── Default empty fields ─────────────────────────────────────────────────────

export const emptySowFields = (clientName: string = "Client"): SowFields => ({
  coverEngagementType: "PENETRATION TESTING ENGAGEMENT",
  coverDocumentTitle: "Statement of Work",
  coverSubtitle: "",
  coverPreparedFor: clientName,
  coverPreparedBy: "Capture The Bug Limited",
  coverDocumentReference: "",
  coverVersionLabel: "1.0",
  coverClassification: "Confidential",
  coverFooterNote: "capturethebug.xyz   ·   Confidential: intended recipient only",
  footerReferenceNote: "",
  innerPageHeaderText: "Statement of Work   ·   Confidential",

  introductionText: `Capture The Bug (CTB) will conduct a penetration test for ${clientName}, covering the agreed scope, testing approach, deliverables, responsibilities, and commercial terms. Appendix A - Rules of Engagement forms part of this Statement of Work and defines the operational conditions for testing.

The assessment is performed remotely through the CTB PTaaS platform against the environment(s) agreed with ${clientName}, using the testing approach set out in the Engagement Structure.

The engagement will identify and validate security weaknesses across authorised targets and provide prioritised remediation guidance. Retesting is included as set out in this document.`,
  objectiveText: `The primary objective of this engagement is to assess the security posture of ${clientName}’s designated target environments, identify vulnerabilities, validate exploitable attack vectors, evaluate business impact, and deliver actionable remediation guidance to strengthen ${clientName}’s defensive baseline.

The engagement also provides ${clientName} with an independent assessment of its externally exposed attack surface to support internal risk-management activities. This assessment does not constitute a formal compliance audit, certification, or attestation, and no such outcome is offered or implied.`,
  targetItems: [],
  authorisedTargets: "",
  scopeBoundaryText: `Testing is limited to the formally authorised targets confirmed by ${clientName} before the engagement begins. Any host, service, application, or address outside the approved target list is out of scope. Individual pages, routes, API paths, parameters, ports, subdomains, and administrative interfaces identified during testing are treated as discovery artefacts for reporting purposes only and do not independently expand the authorised legal scope. Routes discovered within an authorised target may be tested as part of that target; an API, host, or service located outside the authorised target boundary is not brought into scope by its discovery.`,
  rulesOfEngagementSummaryText: `Appendix A - Rules of Engagement forms part of this Statement of Work and records the authorised targets, testing environment, CTB's testing source address, in-scope accounts and named testers, confirmed testing duration, testing window and blackout periods, emergency contacts, prohibited testing activities, and the stop-testing procedure.

Fields in Appendix A marked as to be confirmed depend on information that becomes available during scope validation. They will be completed and confirmed in writing by both parties before testing commences, and completion of Appendix A is a condition precedent to the commencement of testing. Where Appendix A conflicts with the body of this Statement of Work, the body prevails.`,
  testingMethodologyText: `CTB will perform the assessment in alignment with recognised penetration testing guidance, including the Penetration Testing Execution Standard (PTES), NIST SP 800-115, the OWASP Web Security Testing Guide, the OWASP Top 10, the OWASP API Security Top 10, and the OWASP Mobile Application Security Verification Standard (MASVS), where relevant to the authorised attack surface. Testing combines automated discovery and vulnerability analysis with focused manual testing and validation by experienced consultants: automated techniques map the authorised attack surface and identify candidate issues, and manual testing validates exploitability, removes false positives, investigates business-logic weaknesses, and assesses real-world impact. All testing is performed within the authorised boundaries and with reasonable care to minimise disruption to the production environment.`,
  executionLifecycle: [
    { phase: "Reconnaissance & Target Validation", description: "Confirm authorised targets, access methods, test accounts, role configuration, and allowlisting. Map the authorised attack surface." },
    { phase: "Automated Vulnerability Analysis", description: "Identify candidate weaknesses across the authorised targets using automated tooling." },
    { phase: "Manual Penetration Testing", description: "Manual testing of authentication, session management, access control, business logic, privilege escalation, and any sensitive workflows within the agreed constraints." },
    { phase: "Exploitation Validation & Risk Assessment", description: "Safely validate exploitable findings, assess business impact, and assign risk ratings." },
    { phase: "Reporting & Remediation Guidance", description: "Document validated findings with evidence, severity, impact, and actionable remediation guidance." },
    { phase: "Retesting", description: "Validate remediated findings on a per-finding basis as they are resolved by the Client, within the retesting period." }
  ],
  coverageWebText: "",
  coverageApiText: "",
  coveragePaymentText: "",
  coverageNetworkText: "",
  milestoneSchedule: [
    { milestone: "Expected Engagement Initiation Date", schedule: "To be confirmed. Testing begins following execution of this Statement of Work, completion of Appendix A, receipt of payment, and the Client's confirmation that the platform is publicly available." },
    { milestone: "Scope Validation, Target Finalisation & Completion of Appendix A", schedule: "1-2 business days" },
    { milestone: "Testing Execution", schedule: "To be confirmed at scope validation and recorded in Appendix A" },
    { milestone: "Analysis, Validation & Report Drafting", schedule: "2-3 business days" },
    { milestone: "Draft Report Delivery", schedule: "Within 3 business days of testing completion" },
    { milestone: "Client Remediation Window", schedule: "To be confirmed by the Client" },
    { milestone: "Retesting & Validation", schedule: "On request, per finding, throughout the 90-day retesting period following the Project Completion Date" },
    { milestone: "Final Report Delivery", schedule: "Within 2 business days of the Client confirming that remediation and associated retesting are complete" }
  ],
  deliverables: [
    { deliverable: "Draft Penetration Testing Report", description: "A draft report detailing all confirmed findings, severity ratings, supporting evidence, and prioritised remediation guidance, issued for Client review." },
    { deliverable: "Retesting Validation Output", description: "Confirmation of the retesting outcome for each remediated finding submitted for validation, recorded against the finding in the CTB platform." },
    { deliverable: "Final Penetration Testing Report", description: "The finalised report incorporating Client feedback and the outcome of retesting, issued once the Client confirms remediation is complete." },
    { deliverable: "CTB Platform Access", description: "Access to the CTB platform for managing findings and tracking remediation during the engagement and for 90 days from the Project Completion Date." }
  ],
  ptaasPlatformText: `The Client receives access to the CTB platform from engagement commencement. The platform provides engagement visibility, a vulnerability dashboard with finding-level detail and evidence, remediation tracking, per-finding retest submission and outcomes, report artefacts, team access for relevant Client stakeholders, and centralised communication with CTB. Access remains available throughout testing, reporting, remediation coordination, and retesting, and for 90 days from the Project Completion Date, so that it covers the full retesting period during which findings are submitted for validation. Continued access after that point may require a separate agreement or subscription.

Use of the CTB platform is governed by the CTB Customer Terms and Conditions incorporated into this Statement of Work. No separate platform agreement is required, and this Statement of Work takes precedence in the event of conflict.`,
  timelineAssumptionsText: `This indicative timeline assumes timely provision of all required target information, access arrangements, test accounts, allowlisting, and environmental readiness by the Client, and confirmation of the date on which the platform becomes publicly available. Any material change to scope, target availability, or testing dependencies may require the timeline to be revised.`,
  testingStartDate: "",
  testingEndDate: "",
  draftReportDate: "",
  finalReportDate: "",
  retestingEndDate: "",
  assumptions: `The platform is fully publicly available for the duration of the testing window.
Testing is performed against the production environment, as directed by the Client.
Workflows available to the standard access tier direct outbound communications to simulated recipients only, so testing of that tier will not generate live communications to third parties.
Identity-verification functionality is exercised only against the Client's non-billable sandbox path; CTB will not perform identity verification against live or billable services.
The page count, functional inventory, API endpoint inventory, and the services hosted on the authorised external network address have not been confirmed and will be established during scope validation. Representative sampling may be applied where functionality and security controls are substantially identical.
No source code, secure code review, or architecture review is included.`,
  clientDependenciesEnabled: true,
  clientDependencies: `Confirm the date on which the platform becomes publicly available, so the testing window can be scheduled.
Provision and maintain test accounts for the standard and privileged access tiers, release privileged credentials following execution of this Statement of Work, and provision additional accounts on request where required to complete the agreed coverage.
Allowlist CTB's testing source address and confirm any required adjustments to web application firewall, bot protection, or rate-limiting controls.
Ensure the payment path is testable end to end in the Client's Stripe test environment (test mode), covering the checkout, webhook, and settlement paths against Stripe test cards. No funds, payment instruments, or spend cap are required.
Confirm the services hosted on the authorised external network address.
Nominate an emergency contact available during the testing window, and confirm any blackout periods or change windows to be avoided.
Provide a short product walkthrough so that testers understand the intended workflows before testing begins.
Confirm remediation status and submit findings for retesting through the CTB platform during the retesting period.`,
  disclaimersText: `Professional Conduct: CTB will perform the services with reasonable skill, care, and diligence, consistent with recognised industry practices applicable to penetration testing engagements.

Risk Acknowledgement: Testing will be conducted against a production environment. While CTB takes reasonable care to minimise disruption, certain testing activities carry an inherent risk of service degradation or unintended effects, and testing may generate records, notifications, state changes, or transactions within the Client's environment. The Client acknowledges these risks and is responsible for maintaining appropriate backups and operational safeguards during the testing window.

Issue Notification: CTB will promptly notify the Client of any material unintended issue arising from testing, and of any critical vulnerability identified during the engagement that warrants immediate attention.

Client Authority and Responsibility: The Client confirms that it has the authority to authorise testing of the in-scope targets and is responsible for obtaining any third-party authorisations required, including from hosting, infrastructure, payment, identity-verification, or other service providers, before testing begins.

Indemnity: To the extent permitted by law, the Client will indemnify CTB, its employees, and contractors against third-party claims arising from the Client's lack of authority to approve testing of the in-scope targets, except to the extent such claims are finally determined to result from CTB's wilful misconduct or gross negligence.

Confidentiality of Client Information: CTB acknowledges that the platform and the information disclosed during this engagement include commercially sensitive material, including material relating to intellectual property that has not yet been granted. Such information will be handled in accordance with the confidentiality obligations agreed between the parties and the incorporated CTB Customer Terms and Conditions, and disclosed only to CTB personnel engaged in the delivery of this engagement.

Data Handling and Retention: CTB will securely destroy all Client data and testing artefacts within 30 days of issuing the final penetration testing report, and will retain the report for no longer than 12 months unless the parties agree otherwise in writing. A certificate of destruction will be provided on request.

Breach Notification: CTB will notify the Client within 72 hours of becoming aware of any security breach affecting Client data or information handled in connection with this engagement.

Intellectual Property: All pre-existing intellectual property, methodologies, and tooling used in the delivery of the engagement remain the property of CTB. Upon full payment, the Client is granted a non-exclusive, perpetual licence to use the deliverables for its internal security, audit, compliance, and risk management purposes. The Client may share the deliverables with its auditors, regulators, customers, and professional advisors on a need-to-know basis.

Limitation of Assurance: The assessment provides a point-in-time evaluation of the in-scope targets. It does not constitute a warranty that the targets are free of all vulnerabilities, and no assurance of completeness is given or implied. Security posture may change as the environment, configuration, or threat landscape evolves.

Governing Law and Jurisdiction: This Statement of Work is governed by and construed in accordance with the laws of New Zealand, and the parties submit to the exclusive jurisdiction of the New Zealand courts.`,
  standardExclusions: `Any application, environment, domain, subdomain, API, service, or address not explicitly identified as an authorised target.
Internal networks, internal infrastructure, cloud infrastructure assessments, cloud configuration reviews, and underlying hosting platforms.
The systems and infrastructure of third-party payment processors, identity-verification providers, and other third-party service providers.
Testing of the payment path against live or billable transactions; all payment testing is limited to the Client's Stripe test environment (test mode).
Identity verification against live or billable services, and generation of live communications to real third-party recipients.
Denial-of-service, distributed denial-of-service, stress testing, load testing, and other availability-focused testing activities.
Physical security assessments, social engineering, phishing, vishing, and other human-focused security testing activities.
Source code review, secure code review, software composition analysis, architecture review, and threat modelling activities.
Formal compliance assessments, audits, certifications, or attestations against any regulatory or security framework.
Testing of functionality, features, or environments introduced after scope validation unless separately agreed in writing.`,
  additionalExclusions: "",
  engagementModel: "one-time",
  consultantDays: null,
  price: null,
  currency: "NZD",
  paymentTerms:
    "Payment is due in full prior to engagement commencement. GST applies where the Client is a New Zealand resident.",
  engagementStructure: [
    { phase: "Scope Confirmation and Setup", description: "Finalise targets, confirm hosted services, complete Appendix A, provision test accounts, confirm allowlisting", duration: "1-2 business days" },
    { phase: "Baseline Comprehensive Assessment", description: "Unauthenticated and authenticated grey-box testing of the authorised web applications, API and integration services, and external network host", duration: "To be confirmed at scope validation" },
    { phase: "Reporting & QA", description: "Draft report, quality assurance, and final report delivery", duration: "2-3 business days" },
    { phase: "Retesting", description: "Per-finding validation of remediated findings", duration: "90 days from the Project Completion Date" },
    { phase: "Platform Access", description: "Access to the CTB platform for findings and remediation tracking", duration: "Engagement plus 90 days from the Project Completion Date" }
  ],
  generalTerms: [
    { sn: "1", term: "Taxes", details: "The engagement fee treatment for New Zealand Goods and Services Tax (GST) is applied on the basis of the Client's residency and place of supply. Any GST or equivalent tax payable is added to the invoice and shown separately." },
    { sn: "2", term: "Currency", details: "Fees are quoted and payable in the currency stated in the Specific Terms. Payment is to be made to CTB's nominated bank account, with any currency conversion applied at the time of transfer and borne by the Client." },
    { sn: "3", term: "Bank and Transfer Charges", details: "The Client is responsible for all bank fees, wire transfer charges, currency conversion costs, and any intermediary or correspondent bank charges associated with payment. CTB will not bear these charges." },
    { sn: "4", term: "Upfront Payment Requirement", details: "Payment is due in full prior to engagement commencement, unless otherwise agreed in the Specific Terms." },
    { sn: "5", term: "Late Payments", details: "A late payment fee of 5% per month applies to overdue amounts." },
    { sn: "6", term: "Scope Changes", details: "Any change to the agreed scope will be documented and may affect the fee and timeline." },
    { sn: "7", term: "Timeline Dependencies", details: "Timelines are indicative and depend on timely provision of access, credentials, allowlisting, and required information by the Client." }
  ],
  specificTerms: [
    { sn: "1", term: "Payment Terms", details: "100% of the engagement fee is payable upfront. The engagement is scheduled and commences on receipt of cleared funds." },
    { sn: "2", term: "Engagement Commencement", details: "Testing commences once scope is finalised, Appendix A is completed and confirmed in writing, credentials/allowlisting are confirmed, and cleared funds are received." },
    { sn: "3", term: "Engagement Duration", details: "As set out in the Timeline and Deliverables and Engagement Structure sections, subject to scope dependencies." },
    { sn: "4", term: "Refund Policy", details: "Once testing has commenced, fees are non-refundable." },
    { sn: "5", term: "Retesting", details: "Unlimited retesting of findings reported during this engagement is included for 90 days following the Project Completion Date." },
    { sn: "6", term: "Payment Testing", details: "Testing of the payment path is performed in the Client's test environment using test cards. No real funds or transactions will be initiated." },
    { sn: "7", term: "Named Tester Access", details: "Administrative access is restricted to named CTB testers nominated by CTB and accepted by the Client." }
  ],
  indicativeDeliveryDuration: "To be confirmed following scope validation",
  baselineAssessmentDuration: "To be confirmed at scope validation",
  appendixRoEIntro: `This Appendix forms part of this Statement of Work and sets out the operational rules under which testing is authorised and performed. Fields marked "To be confirmed" will be completed and confirmed in writing by both parties before testing commences; completion of this Appendix is a condition precedent to the commencement of testing. Where this Appendix conflicts with the body of this Statement of Work, the body prevails.`,
  testingEnvironment: "Production",
  servicesOnInfrastructure: "To be confirmed before testing commences.",
  publicAvailabilityDate: "To be confirmed by the Client.",
  thirdPartyAuthorisationsText: "The Client confirms it has obtained any third-party approvals reasonably required for the authorised testing activities, including where applicable from hosting, infrastructure, payment, or identity-verification providers, before testing begins.",
  testingSourceIp: "81.181.51.121/32",
  wafRateLimitingNote: "Controls that would materially impede testing to be adjusted or bypassed for the allowlisted source by agreement. Detail to be confirmed.",
  testingWindow: "To be confirmed before testing commences.",
  blackoutPeriods: "To be confirmed before testing commences.",
  projectCompletionDateNote: "The date on which CTB issues the draft penetration testing report, unless a different date is agreed and recorded here.",
  testAccountsNote: "To be confirmed",
  namedTesters: "To be confirmed",
  standardAccessAccount: "pentest@capturethebug.xyz",
  privilegedAccessAccount: "jk@capturethebug.xyz",
  paymentTestingNote: "Performed exclusively in the Client's Stripe test environment (test mode), exercising the full checkout, webhook, and settlement paths using Stripe test cards. No real transaction value; no spend cap, funding, or test-transaction handling arrangements are required.",
  identityVerificationNote: "Exercised only against the Client's non-billable sandbox path.",
  clientEmergencyContact: "To be confirmed - name, role, phone, email.",
  ctbEngagementLead: "To be confirmed - name, phone, email.",
  prohibitedActivitiesText: `Intentionally destroy, encrypt, bulk-download, or otherwise remove Client production data from the Client environment, except for the minimum evidence reasonably necessary to demonstrate and document a confirmed finding, which will be recorded within the finding.
Modify or delete Client user accounts, records, or configuration, other than accounts and records created by CTB for testing purposes.
Install persistence mechanisms, backdoors, or any change intended to survive the testing window. Any test artefact created by CTB will be recorded and removed at the conclusion of testing.
Use credentials, tokens, or access obtained during testing beyond what is required to demonstrate the finding.
Test any host, service, application, or address not listed as an authorised target.`,
  stopTestingProcedureText: `Requesting a pause: Either party may request an immediate pause at any time by telephoning the other party's emergency contact, followed by written confirmation by email.
CTB response: CTB will suspend all testing activity immediately on receipt of a pause request and confirm suspension in writing.
CTB-initiated pause: CTB will pause testing on its own initiative, and notify the Client without delay, where it identifies a material unintended effect on the production environment, a critical vulnerability warranting immediate attention, or evidence of a pre-existing compromise.
Resumption: Testing resumes only once both parties have agreed in writing, and only against the targets confirmed as ready.
Effect on scope and timeline: Time lost to a pause does not reduce the agreed scope. The timeline will be adjusted in accordance with the Timeline Dependencies term.`,
  clientSignerName: "",
  clientSignerTitle: "",
  clientEmail: "",
  clientPhone: "",
  clientSignedAt: null,
  clientBillingName: "",
  clientRegisteredCompany: "",
  clientCountryOfIncorporation: "",
  clientBillingAddress: "",
  clientTaxNumber: "",
  clientBillingEmail: "",
  clientBillingPhone: "",
  ctbSignerName: "Ankita Dhakar",
  ctbSignerTitle: "CEO",
  ctbSignerPhone: "+64-221994320",
  ctbSignerEmail: "ankita@capturethebug.xyz",
  ctbSignatureBase64: null,
  ctbSignedAt: null
});

// ─── API functions ────────────────────────────────────────────────────────────

/**
 * GET /questionnaires/responses/:id/sow
 * Fetch the current SoW state for a scoping session.
 */
export const getSow = async (id: string): Promise<SowState> => {
  const res = await instance.get<SowState>(`/questionnaires/responses/${id}/sow`);
  return res.data;
};

/**
 * POST /questionnaires/responses/:id/sow
 * Create or save-draft the SoW (status → DRAFT, version incremented).
 */
export const saveSowDraft = async (
  id: string,
  fields: SowFields
): Promise<SowState & { message: string }> => {
  const res = await instance.post(`/questionnaires/responses/${id}/sow`, {
    fields
  });
  return res.data;
};

/**
 * POST /questionnaires/responses/:id/sow/submit
 * Advance SoW from DRAFT → IN_REVIEW.
 */
export const submitSowForReview = async (
  id: string
): Promise<{ message: string; sow_status: SowStatus }> => {
  const res = await instance.post(
    `/questionnaires/responses/${id}/sow/submit`
  );
  return res.data;
};

/**
 * POST /questionnaires/responses/:id/sow/approve
 * Advance SoW from IN_REVIEW → FINAL.
 */
export const approveSow = async (
  id: string
): Promise<{ message: string; sow_status: SowStatus }> => {
  const res = await instance.post(
    `/questionnaires/responses/${id}/sow/approve`
  );
  return res.data;
};

/**
 * POST /questionnaires/responses/:id/sow/reopen
 * Return SoW from IN_REVIEW or FINAL → DRAFT for further editing.
 */
export const reopenSow = async (
  id: string
): Promise<{
  message: string;
  sow_status: SowStatus;
  sow_version?: number;
  sow_data?: SowBlob | null;
}> => {
  const res = await instance.post(
    `/questionnaires/responses/${id}/sow/reopen`
  );
  return res.data;
};
